'use client';

/**
 * HealthConsentReconciler — propagates a pre-login marketing-consent choice
 * into the customer's `health_consent` profile field once, at login (MWP-1023).
 *
 * Marketing consent granted while logged out lives only in localStorage;
 * `updateHealthConsent` bails when unauthenticated, so the grant never reaches
 * the profile. The profile field is the cross-repo Article-9 gate that decides
 * whether health-identifying line items appear in transactional emails — an
 * under-set profile silently strips items from a consented customer's receipts.
 *
 * localStorage is browser-only, so the trigger must be client-side. This
 * component mounts wherever the customer is authenticated (both the `(main)`
 * and `(checkout)` layouts) and fires once per customer. It is deliberately
 * **not** gated on `braze_enabled` — the profile field feeds email gating
 * independently of the Braze SDK.
 *
 * Both login paths are covered without hooking their (differing) completion
 * flows: brand-emailpass redirects server-side and Cognito calls
 * `router.refresh()`; either way the authenticated layout re-renders and mounts
 * this component.
 *
 * The layout passes the profile's already-fetched (cached) `health_consent`, so
 * the common already-reconciled case is decided entirely on the client and
 * makes **zero** server calls. Only an apparent mismatch invokes the server
 * action, which re-reads the profile fresh and re-decides before writing — so a
 * withdrawal recorded on another device is never resurrected from a stale
 * cache. Because the client short-circuit can only *skip a genuine no-op* (see
 * `resolveHealthConsentReconcile`), the fresh server read remains authoritative.
 */

import { getConsentPreferences } from '@lib/analytics/consent';
import { reconcileHealthConsentOnLogin } from '@lib/data/customer';
import {
  deviceMarketingConsent,
  resolveHealthConsentReconcile,
} from '@lib/util/health-consent';
import { useEffect, useRef } from 'react';

type Props = {
  /** Medusa customer ID — present only when authenticated. */
  customerId: string;
  /**
   * Current profile `health_consent`, normalised from the layout's cached
   * customer fetch: `true`/`false` when set, `undefined` when never set.
   * Used only for the client-side short-circuit; the server action re-reads
   * the authoritative value before writing.
   */
  profileHealthConsent: boolean | undefined;
};

const HealthConsentReconciler = ({
  customerId,
  profileHealthConsent,
}: Props) => {
  const reconciledFor = useRef<string | null>(null);

  useEffect(() => {
    // Reconcile once per authenticated customer for this component's lifetime.
    if (reconciledFor.current === customerId) {
      return;
    }
    reconciledFor.current = customerId;

    // Derive the device's marketing choice. `null` means no determinable
    // choice (no prefs, or legacy/malformed data without a boolean
    // `advertising`) — we must not touch the Article-9 profile field, since an
    // indeterminate choice is neither a grant nor a withdrawal.
    const device = deviceMarketingConsent(getConsentPreferences());
    if (device === null) {
      return;
    }

    // Cheap client-side pre-check against the cached profile value. When the
    // profile already matches (or an explicit withdrawal must be preserved),
    // this is a no-op and we avoid a server round-trip entirely.
    if (resolveHealthConsentReconcile(device, profileHealthConsent) === null) {
      return;
    }

    // Apparent mismatch — hand off to the server action, which re-reads the
    // profile fresh and re-decides before writing. Fire-and-forget: on failure
    // localStorage remains the device's source of truth and the reconcile
    // retries on the next login.
    void reconcileHealthConsentOnLogin(device).catch(() => {
      // Network or auth error — safe to ignore, see above.
    });
  }, [customerId, profileHealthConsent]);

  return null;
};

export default HealthConsentReconciler;
